import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import { SignupAddressStore } from './address/signup-address.store';
import { ContactFormStore } from './contact/contact-form.store';
import { SignupEnrollmentService } from './enroll/signup-enrollment.service';
import { SignupSubmitService } from './intake/signup-submit.service';

/** Prefix for any /signup value kept in local/session storage, so leaving the flow can clear them all. */
export const SIGNUP_STORAGE_PREFIX = 'signup:';

@Injectable({ providedIn: 'root' })
export class SignupResetService {
  private addressStore = inject(SignupAddressStore);
  private contactStore = inject(ContactFormStore);
  private enrollment = inject(SignupEnrollmentService);
  private submit = inject(SignupSubmitService);

  /** Address-first landing page the "Back" and "change" (address) links return to. */
  readonly landingUrl = environment.signupLandingUrl;

  /** True from the Back click until the page unloads; the view freezes (inert, dimmed) meanwhile. */
  readonly leaving = signal(false);

  /** Clears the sign-up stores and every `signup:` storage key; auth and signing keys are left alone. */
  reset(): void {
    this.addressStore.reset();
    this.contactStore.reset();
    this.enrollment.reset();
    this.submit.clearSubmitted();
    this.clearStorage();
  }

  /**
   * Click handler for a link to `landingUrl`. A Cmd/Ctrl/Shift/Alt/middle-click is left to the browser
   * (opens a new tab); this page stays as-is.
   */
  onLandingLinkClick(event: MouseEvent): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    this.exitToLanding();
  }

  /**
   * Leaves for the address-first landing page (www.kin.com on production only). Storage is cleared now, but
   * the stores only on `pagehide`: emptying them while the page is still painted re-renders it without an
   * address (→ a flash of the waitlist) before the browser navigates.
   */
  exitToLanding(): void {
    if (this.leaving()) return;
    this.leaving.set(true);
    this.clearStorage();
    window.addEventListener('pagehide', () => this.reset(), { once: true });
    // `window.open(url, '_self')` rather than `location.href`: same-tab navigation that is spy-able in tests.
    window.open(this.landingUrl, '_self');
  }

  private clearStorage(): void {
    for (const storage of [localStorage, sessionStorage]) {
      const keys = Array.from({ length: storage.length }, (_, i) => storage.key(i));
      keys.filter(key => key?.startsWith(SIGNUP_STORAGE_PREFIX)).forEach(key => storage.removeItem(key!));
    }
  }
}
